/**
 * I numeri di telefono di una persona della Rete (24/10/2026).
 *
 * Prima la scheda aveva UN campo `telefono`: chi ha un fisso e un cellulare (Luciano Murgia: il fisso che non risponde
 * mai, il cellulare mandato su LinkedIn) doveva perderne uno. Ora la scheda ha `telefoni`, un elenco: il PRIMO è quello
 * principale (i bottoni «Chiama» e «WhatsApp» in cima alla scheda, la ricerca sul web), gli altri restano tutti, e ogni
 * numero può avere un'etichetta libera («cellulare», «fisso», «ufficio»). L'etichetta è un dato, non un elenco chiuso:
 * come i tag della Rete, non esiste nel codice.
 *
 * Modulo PURO, senza import: lo usano la console (`@shared/rete`), le funzioni del server (`_shared-pkg/rete`, copiato
 * da `npm run sync:shared`) e lo script di migrazione (`scripts/rete-telefoni-in-elenco.cjs`, dal compilato).
 *
 * ⚠️ La regola «stesso numero» è la stessa di `cifre()` in `~/.claude/skills/probusiness/script/rete.py` (le ultime 9
 * cifre): la skill è in Python e non può importare questo file. Chi cambia l'una cambia l'altra.
 *
 * Doc canonico: docs/01-ARCHITECTURE/RETE-CONTATTI-MODEL.md
 */

/** Un numero della persona, così com'è scritto su `platform_rete_persone/{id}.telefoni[]`. */
export interface TelefonoRete {
  /** Il numero come lo si scrive («+39 349 065 5432», «0782 93205»): si confronta per cifre, non per testo. */
  numero: string;
  /** Che numero è, a parole libere («cellulare», «fisso», «ufficio»). Facoltativa. */
  etichetta?: string;
}

/**
 * Le cifre con cui due numeri si confrontano: le ultime 9, così «+39 349 065 5432», «0039 3490655432» e «349 065 5432»
 * sono lo stesso numero. Sotto le 9 cifre si confronta tutto quello che c'è.
 */
export function cifreDelNumero(numero: string | null | undefined): string {
  const solo = (numero ?? '').replace(/\D/g, '');
  return solo.length >= 9 ? solo.slice(-9) : solo;
}

/** true se i due numeri sono lo stesso (per cifre). Due numeri senza cifre non sono mai «lo stesso». */
export function stessoNumero(a: string | null | undefined, b: string | null | undefined): boolean {
  const ca = cifreDelNumero(a);
  return ca.length > 0 && ca === cifreDelNumero(b);
}

/**
 * L'elenco pronto da scrivere: numeri e etichette senza spazi ai bordi, via le righe senza numero e via i doppioni
 * (vince il primo, che è anche il principale), via le etichette vuote. Non cambia l'ordine.
 */
export function telefoniPuliti(elenco: readonly TelefonoRete[] | null | undefined): TelefonoRete[] {
  const visti = new Set<string>();
  const risultato: TelefonoRete[] = [];
  for (const voce of elenco ?? []) {
    const numero = (voce?.numero ?? '').trim();
    const cifre = cifreDelNumero(numero);
    if (!numero || visti.has(cifre)) continue;
    if (cifre) visti.add(cifre);
    const etichetta = (voce?.etichetta ?? '').trim();
    risultato.push(etichetta ? { numero, etichetta } : { numero });
  }
  return risultato;
}

/**
 * I numeri di una scheda letta da Firestore senza tipi (il server, i trigger): solo le voci con un numero vero, in
 * ordine. Un valore che non è un elenco dà un elenco vuoto.
 */
export function numeriDellaScheda(telefoni: unknown): string[] {
  if (!Array.isArray(telefoni)) return [];
  return telefoni
    .map((t) => (t && typeof t === 'object' && typeof (t as { numero?: unknown }).numero === 'string'
      ? (t as { numero: string }).numero.trim()
      : ''))
    .filter(Boolean);
}

/** Il numero per i collegamenti `tel:` e `wa.me`: solo cifre e il «+» iniziale. */
export function numeroPerCollegamento(numero: string): string {
  return numero.replace(/[^\d+]/g, '');
}
